// ============================================================
// FICHEIRO: frontend/src/pages/admin/LandingContentTab.js
// Cria este ficheiro novo na pasta admin/
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Globe, Plus, Trash, FloppyDisk, ArrowCounterClockwise, Star, WhatsappLogo, PawPrint, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_CONTENT = {
  hero_title: 'Encontre o Pet Sitter Perfeito',
  hero_subtitle: 'Conectamos donos de animais com pet sitters e passeadores de cães verificados em todo o Portugal.',
  hero_badge: 'O melhor cuidado para o seu melhor amigo 🐾',
  hero_cta_primary: 'Começar Agora — É Grátis!',
  hero_cta_secondary: 'Ver Prestadores',
  hero_social_proof: '+2.000 donos já confiam em nós',
  stat_providers: '500+', stat_providers_label: 'Prestadores Verificados',
  stat_animals: '2.000+', stat_animals_label: 'Animais Felizes',
  stat_rating: '4.9★', stat_rating_label: 'Avaliação Média',
  stat_coverage: '100%', stat_coverage_label: 'Portugal',
  whatsapp_number: '351928314198',
  whatsapp_message: 'Olá! Gostaria de saber mais sobre o PasseioPerfeito 🐾',
  whatsapp_section_title: 'Tem dúvidas? Fale connosco agora!',
  whatsapp_section_subtitle: 'A nossa equipa está disponível para o ajudar a encontrar o pet sitter ideal para o seu animal.',
  cta_section_title: 'Pronto para começar?',
  cta_section_subtitle: 'Junte-se a milhares de donos de animais que já encontraram o pet sitter perfeito em Portugal.',
  provider_section_title: 'Transforme o amor pelos animais num negócio',
  provider_section_subtitle: 'Junte-se à maior rede de pet sitters de Portugal.',
  testimonials: [
    { id: 't1', name: 'Ana Ribeiro', location: 'Porto', text: 'Encontrei a pet sitter perfeita para o meu Buddy. Profissional, carinhosa e muito responsável!', rating: 5, avatar: 'AR', pet: 'Dona do Buddy 🐕' },
    { id: 't2', name: 'Carlos Mendes', location: 'Lisboa', text: 'Serviço incrível! O meu gato ficou aos cuidados da Margarida enquanto estava em viagem.', rating: 5, avatar: 'CM', pet: 'Dono do Mimi 🐱' },
    { id: 't3', name: 'Sofia Ferreira', location: 'Braga', text: 'A melhor plataforma de pet sitters em Portugal. Fácil de usar e preços acessíveis!', rating: 5, avatar: 'SF', pet: 'Dona da Luna 🐶' },
  ],
};

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block text-muted-foreground">{label}</label>
      {type === 'textarea' ? (
        <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} className="text-sm" />
      ) : (
        <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="text-sm" />
      )}
    </div>
  );
}

export default function LandingContentTab() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/landing-content`);
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setContent(prev => ({ ...prev, ...data }));
        }
      }
    } catch (e) {
      toast.error('Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const set = (key, value) => setContent(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/landing-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(content),
      });
      if (res.ok) {
        toast.success('Conteúdo guardado com sucesso! 🎉');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao guardar');
      }
    } catch (e) {
      toast.error('Erro ao guardar conteúdo');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(DEFAULT_CONTENT);
    toast.info('Conteúdo reposto para os valores padrão (não guardado)');
  };

  const addTestimonial = () => {
    const newT = { id: `t_${Date.now()}`, name: '', location: '', text: '', rating: 5, avatar: '', pet: '' };
    set('testimonials', [...(content.testimonials || []), newT]);
  };

  const updateTestimonial = (id, field, value) => {
    set('testimonials', content.testimonials.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const removeTestimonial = (id) => {
    set('testimonials', content.testimonials.filter(t => t.id !== id));
  };

  const sections = [
    { id: 'hero', label: 'Hero', icon: PawPrint },
    { id: 'stats', label: 'Estatísticas', icon: ChartBar },
    { id: 'whatsapp', label: 'WhatsApp', icon: WhatsappLogo },
    { id: 'testimonials', label: 'Testemunhos', icon: Star },
    { id: 'cta', label: 'Chamadas à Ação', icon: Globe },
  ];

  if (loading) return <div className="space-y-3">{[1,2,3].map(i=><Card key={i} className="h-20 animate-pulse bg-muted"/>)}</div>;

  return (
    <div className="space-y-6" data-testid="admin-landing-content">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Globe weight="duotone" className="w-5 h-5 text-primary"/>Conteúdo do Site</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Edite os textos e conteúdos que aparecem na página principal</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} className="rounded-full">
            <ArrowCounterClockwise className="w-4 h-4 mr-1"/>Repor
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full" data-testid="save-landing-content-btn">
            <FloppyDisk className="w-4 h-4 mr-1"/>{saving ? 'A guardar...' : 'Guardar Alterações'}
          </Button>
        </div>
      </div>

      {/* Section Nav */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeSection === s.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
              <Icon className="w-4 h-4"/>{s.label}
            </button>
          );
        })}
      </div>

      {/* Hero Section */}
      {activeSection === 'hero' && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><PawPrint weight="duotone" className="w-5 h-5 text-primary"/>Secção Hero (Topo da Página)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Título Principal" value={content.hero_title} onChange={v => set('hero_title', v)} placeholder="Encontre o Pet Sitter Perfeito" />
            <Field label="Subtítulo" type="textarea" value={content.hero_subtitle} onChange={v => set('hero_subtitle', v)} />
            <Field label="Badge (texto pequeno acima do título)" value={content.hero_badge} onChange={v => set('hero_badge', v)} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Botão Principal" value={content.hero_cta_primary} onChange={v => set('hero_cta_primary', v)} />
              <Field label="Botão Secundário" value={content.hero_cta_secondary} onChange={v => set('hero_cta_secondary', v)} />
            </div>
            <Field label="Prova Social (texto junto aos avatares)" value={content.hero_social_proof} onChange={v => set('hero_social_proof', v)} />
          </CardContent>
        </Card>
      )}

      {/* Stats Section */}
      {activeSection === 'stats' && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ChartBar weight="duotone" className="w-5 h-5 text-primary"/>Estatísticas</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { valKey: 'stat_providers', labelKey: 'stat_providers_label', title: 'Prestadores' },
                { valKey: 'stat_animals', labelKey: 'stat_animals_label', title: 'Animais' },
                { valKey: 'stat_rating', labelKey: 'stat_rating_label', title: 'Avaliação' },
                { valKey: 'stat_coverage', labelKey: 'stat_coverage_label', title: 'Cobertura' },
              ].map(stat => (
                <div key={stat.valKey} className="p-4 border rounded-lg space-y-3">
                  <p className="text-sm font-semibold text-muted-foreground">{stat.title}</p>
                  <Field label="Valor (ex: 500+)" value={content[stat.valKey]} onChange={v => set(stat.valKey, v)} />
                  <Field label="Etiqueta (ex: Prestadores Verificados)" value={content[stat.labelKey]} onChange={v => set(stat.labelKey, v)} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* WhatsApp Section */}
      {activeSection === 'whatsapp' && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><WhatsappLogo weight="duotone" className="w-5 h-5 text-green-500"/>WhatsApp</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Número WhatsApp (com código país, ex: 351928314198)" value={content.whatsapp_number} onChange={v => set('whatsapp_number', v)} />
            <Field label="Mensagem pré-preenchida" value={content.whatsapp_message} onChange={v => set('whatsapp_message', v)} />
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold">Secção WhatsApp na página</p>
              <Field label="Título da secção" value={content.whatsapp_section_title} onChange={v => set('whatsapp_section_title', v)} />
              <Field label="Subtítulo da secção" type="textarea" value={content.whatsapp_section_subtitle} onChange={v => set('whatsapp_section_subtitle', v)} />
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg text-sm text-green-700 dark:text-green-400">
              Link gerado: <a href={`https://wa.me/${content.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="underline">https://wa.me/{content.whatsapp_number}</a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Testimonials Section */}
      {activeSection === 'testimonials' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{(content.testimonials||[]).length} testemunho(s)</p>
            <Button onClick={addTestimonial} size="sm" variant="outline" className="rounded-full">
              <Plus className="w-4 h-4 mr-1"/>Adicionar Testemunho
            </Button>
          </div>
          {(content.testimonials || []).map((t, idx) => (
            <Card key={t.id} data-testid={`testimonial-card-${t.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Testemunho #{idx + 1}</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => removeTestimonial(t.id)} className="text-destructive hover:text-destructive h-8 w-8">
                    <Trash className="w-4 h-4"/>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Nome" value={t.name} onChange={v => updateTestimonial(t.id, 'name', v)} placeholder="Ana Ribeiro" />
                  <Field label="Cidade" value={t.location} onChange={v => updateTestimonial(t.id, 'location', v)} placeholder="Porto" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Iniciais (Avatar, ex: AR)" value={t.avatar} onChange={v => updateTestimonial(t.id, 'avatar', v)} placeholder="AR" />
                  <Field label="Pet (ex: Dona do Buddy 🐕)" value={t.pet} onChange={v => updateTestimonial(t.id, 'pet', v)} placeholder="Dona do Buddy 🐕" />
                </div>
                <Field label="Texto do Testemunho" type="textarea" value={t.text} onChange={v => updateTestimonial(t.id, 'text', v)} placeholder="Escreva o testemunho aqui..." />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Avaliação:</span>
                  {[1,2,3,4,5].map(star => (
                    <button key={star} onClick={() => updateTestimonial(t.id, 'rating', star)}
                      className={`text-xl transition-colors ${star <= (t.rating||5) ? 'text-yellow-400' : 'text-gray-300'}`}>★</button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          {(content.testimonials||[]).length === 0 && (
            <Card><CardContent className="py-12 text-center">
              <Star weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4"/>
              <p className="text-muted-foreground">Nenhum testemunho. Clique em "Adicionar Testemunho".</p>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* CTA Section */}
      {activeSection === 'cta' && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe weight="duotone" className="w-5 h-5 text-primary"/>Chamadas à Ação</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-3 p-4 border rounded-lg">
              <p className="text-sm font-semibold">Secção Final (fundo verde)</p>
              <Field label="Título" value={content.cta_section_title} onChange={v => set('cta_section_title', v)} />
              <Field label="Subtítulo" type="textarea" value={content.cta_section_subtitle} onChange={v => set('cta_section_subtitle', v)} />
            </div>
            <div className="space-y-3 p-4 border rounded-lg">
              <p className="text-sm font-semibold">Secção Para Prestadores</p>
              <Field label="Título" value={content.provider_section_title} onChange={v => set('provider_section_title', v)} />
              <Field label="Subtítulo" type="textarea" value={content.provider_section_subtitle} onChange={v => set('provider_section_subtitle', v)} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button at bottom */}
      <div className="flex justify-end pt-2 border-t">
        <Button onClick={handleSave} disabled={saving} size="lg" className="rounded-full px-8">
          <FloppyDisk className="w-5 h-5 mr-2"/>{saving ? 'A guardar...' : 'Guardar Todas as Alterações'}
        </Button>
      </div>
    </div>
  );
}
